import * as React from 'react';
import './fields.scss';
import { Switch, Collapse } from '@mui/material';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { FeatureEnum, PermissionEnum } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import PermissionBadge from '../../PermissionBadge';

export interface PermissionsFieldProps {
    id?: string,
    label?: string,
    isProfessional?: boolean,
    values: PermissionEnum[],
    features: FeatureEnum[],
    onChange: (values: PermissionEnum[]) => void,
}

export const PermissionsField = (props: PermissionsFieldProps) => {
    const {
        id,
        label,
        values,
        isProfessional,
        features,
        onChange,
    } = props;

    const t = useTranslations();
    const [expanded, setExpanded] = React.useState(false);

    const togglePermission = (permission: PermissionEnum) => onChange(values.includes(permission)
            ? values.filter(p => p !== permission)
            : values.concat(permission));

    const listOfPermissions: PermissionEnum[] = [
        PermissionEnum.Administrator,
        PermissionEnum.PricesAccess,
        PermissionEnum.PricesManagement,
        PermissionEnum.ProfessionalsManagement,
        PermissionEnum.AppointmentsAccess,
        PermissionEnum.AppointmentsManagement,
        PermissionEnum.ClientsAccess,
        isProfessional && PermissionEnum.MyClientsManagement,
        PermissionEnum.ClientsManagement,
        features.includes(FeatureEnum.Reports) && PermissionEnum.ClientsReports,
        PermissionEnum.ClientsChat,
        features.includes(FeatureEnum.CRM) && PermissionEnum.CampaignsManagement,
        features.includes(FeatureEnum.Analytics) && PermissionEnum.Analytics,
        PermissionEnum.DoNotAllowSkipAvailability,
        PermissionEnum.DoNotAllowToConfirmAppointments,
        PermissionEnum.DoNotShowPrices,
        PermissionEnum.DoNotCreateOrEditAppointments,
    ].filter((v): v is PermissionEnum => v !== false)

    const descriptionMap: Partial<Record<PermissionEnum, TK>> = {
        [PermissionEnum.Administrator]: TK.permissionAdministratorDescription,
        [PermissionEnum.PricesAccess]: TK.permissionPricesAccessDescription,
        [PermissionEnum.PricesManagement]: TK.permissionPricesManagementDescription,
        [PermissionEnum.ProfessionalsManagement]: TK.permissionProfessionalsManagementDescription,
        [PermissionEnum.ClientsAccess]: TK.permissionClientsAccessDescription,
        [PermissionEnum.ClientsReports]: TK.permissionClientsReportsDescription,
        [PermissionEnum.ClientsManagement]: TK.permissionClientsManagementDescription,
        [PermissionEnum.MyClientsManagement]: TK.permissionMyClientsManagementDescription,
        [PermissionEnum.ClientsChat]: TK.permissionClientsChatDescription,
        [PermissionEnum.AppointmentsAccess]: TK.permissionAppointmentsAccessDescription,
        [PermissionEnum.AppointmentsManagement]: TK.permissionAppointmentsManagementDescription,
        [PermissionEnum.CampaignsManagement]: TK.permissionCampaignsManagementDescription,
        [PermissionEnum.Analytics]: TK.permissionAnalyticsDescription,
        [PermissionEnum.DoNotAllowSkipAvailability]: TK.permissionDoNotAllowSkipAvailabilityDescription,
        [PermissionEnum.DoNotAllowToConfirmAppointments]: TK.permissionDoNotAllowToConfirmAppointmentsDescription,
        [PermissionEnum.DoNotShowPrices]: TK.permissionDoNotShowPricesDescription,
        [PermissionEnum.DoNotCreateOrEditAppointments]: TK.permissionDoNotCreateOrEditAppointmentsDescription,
    };

    // Sub-permissions that appear only in the collapsed view
    const subPermissions: Partial<Record<PermissionEnum, PermissionEnum[]>> = {
        [PermissionEnum.PricesAccess]: [
            PermissionEnum.DoNotAllowToEmitInvoices,
            PermissionEnum.DoNotAllowToCancelInvoices,
        ],
    };

    const labelMap: Partial<Record<PermissionEnum, string>> = {
        [PermissionEnum.Administrator]: t(TK.administrator),
        [PermissionEnum.PricesAccess]: `${t(TK.prices)} / ${t(TK.invoices)}`,
        [PermissionEnum.PricesManagement]: t(TK.billing),
        [PermissionEnum.ProfessionalsManagement]: t(TK.professionals),
        [PermissionEnum.ClientsAccess]: `${t(TK.clients)} (${t(TK.readOnly)})`,
        [PermissionEnum.ClientsReports]: `${t(TK.reports)} / ${t(TK.documents)}`,
        [PermissionEnum.ClientsManagement]: t(TK.clients),
        [PermissionEnum.MyClientsManagement]: `${t(TK.clients)} (${t(TK.onlyMyOwn)})`,
        [PermissionEnum.ClientsChat]: `${t(TK.clients)} ${t(TK.chat)}`,
        [PermissionEnum.AppointmentsAccess]: `${t(TK.calendar)} (${t(TK.readOnly)})`,
        [PermissionEnum.AppointmentsManagement]: t(TK.calendar),
        [PermissionEnum.CampaignsManagement]: t(TK.campaigns),
        [PermissionEnum.Analytics]: t(TK.analytics),
        [PermissionEnum.DoNotAllowSkipAvailability]: t(TK.doNotAllowSkipAvailability),
        [PermissionEnum.DoNotAllowToConfirmAppointments]: t(TK.doNotAllowToConfirmAppointments),
        [PermissionEnum.DoNotShowPrices]: t(TK.doNotShowPrices),
        [PermissionEnum.DoNotCreateOrEditAppointments]: t(TK.doNotCreateOrEditAppointments),
        [PermissionEnum.DoNotAllowToEmitInvoices]: t(TK.allowInvoicesEmission),
        [PermissionEnum.DoNotAllowToCancelInvoices]: t(TK.allowInvoicesCancellation),
    };

    return (
        <div>
            {label && <label className="permissionsLabel">{label}</label>}

            {/* Icon View */}
            <div className="permissionsIconsView">
                {isProfessional && (
                    <div className="permissionIcon isLocked">
                        <PermissionBadge permission="Professional" />
                    </div>
                )}
                {listOfPermissions.map(permission => {
                    const isSelected = values.includes(permission);
                    return (
                        <div
                            key={permission.toString()}
                            className={`permissionIcon ${isSelected ? 'isSelected' : ''}`}
                            onClick={() => togglePermission(permission)}
                        >
                            <PermissionBadge permission={permission} />
                        </div>
                    );
                })}
            </div>

            {/* See Details Toggle */}
            <button
                type="button"
                className={`permissionsToggleButton ${expanded ? 'isExpanded' : ''}`}
                onClick={() => setExpanded(!expanded)}
            >
                {expanded ? t(TK.hideDetails) : t(TK.seeDetails)}
                <ExpandMoreIcon className="toggleIcon" />
            </button>

            {/* Detailed List View */}
            <Collapse in={expanded}>
                <div className="permissionsList">
                    {isProfessional && (
                        <div className="permissionsRow isLocked">
                            <div className="permissionsIconInRow">
                                <PermissionBadge permission="Professional" />
                            </div>
                            <div className="permissionsInfo">
                                <div className="permissionsTitle">{t(TK.professional)}</div>
                                <div className="permissionsDescription">{t(TK.permissionProfessionalDescription)}</div>
                            </div>
                            <Switch checked disabled className="permissionsSwitch" />
                        </div>
                    )}
                    {listOfPermissions.map(permission => {
                        const descriptionKey = descriptionMap[permission];
                        const permissionLabel = labelMap[permission] ?? PermissionEnum[permission];
                        const isChecked = values.includes(permission);

                        return (
                            <React.Fragment key={permission.toString()}>
                                <div className="permissionsRow">
                                    <div className="permissionsIconInRow">
                                        <PermissionBadge permission={permission} />
                                    </div>
                                    <div className="permissionsInfo">
                                        <div className="permissionsTitle">{permissionLabel}</div>
                                        {descriptionKey && (
                                            <div className="permissionsDescription">{t(descriptionKey)}</div>
                                        )}
                                    </div>
                                    <Switch
                                        id={id ? `${id}-${PermissionEnum[permission]}-toggle` : undefined}
                                        checked={isChecked}
                                        onChange={() => togglePermission(permission)}
                                        className="permissionsSwitch"
                                        inputProps={{ 'aria-label': permissionLabel }}
                                    />
                                </div>
                                {/* Render sub-permissions for PricesAccess */}
                                {!!subPermissions[permission]?.length && isChecked && subPermissions[permission].map(subPermission => {
                                    const subDescriptionKey = descriptionMap[subPermission];
                                    const subPermissionLabel = labelMap[subPermission] ?? PermissionEnum[subPermission];
                                    // Inverted logic: checked when permission is NOT in the values array
                                    const isSubChecked = !values.includes(subPermission);

                                    return (
                                        <div className="permissionsRow" key={subPermission.toString()} style={{ paddingLeft: 'var(--spacer-L)', paddingTop: 0, paddingBottom: 0 }}>
                                            <div className="permissionsInfo">
                                                <div className="permissionsTitle">{subPermissionLabel}</div>
                                                {subDescriptionKey && (
                                                    <div className="permissionsDescription">{t(subDescriptionKey)}</div>
                                                )}
                                            </div>
                                            <Switch
                                                id={id ? `${id}-${PermissionEnum[subPermission]}-toggle` : undefined}
                                                checked={isSubChecked}
                                                onChange={() => togglePermission(subPermission)}
                                                className="permissionsSwitch"
                                                inputProps={{ 'aria-label': subPermissionLabel }}
                                            />
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </Collapse>
        </div>
    );
}
